import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileText, Images } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import QuickQuote from "@/components/home/QuickQuote";
import { getProduct, PRODUCTS } from "@/data/products";
import { PRODUCTS_EN } from "@/data/productsEn";
import { useIsEnglish } from "@/lib/locale";
import { staticMedia } from "@/data/galleryMedia";
import NotFound from "@/pages/NotFound";

const BASE_URL = "https://www.naklejki-3d.com.pl";

const galleryImages = staticMedia.filter((m) => m.type === "image").slice(0, 6);

interface ProductPageProps {
  slug: string;
}

/** Wspólny szablon podstrony produktowej – jeden produkt = jeden URL = jedna fraza. */
const ProductPage = ({ slug }: ProductPageProps) => {
  const isEn = useIsEnglish();
  const base = getProduct(slug);
  if (!base) return <NotFound />;

  const translation = isEn ? PRODUCTS_EN[slug] : undefined;
  const product = translation ? { ...base, ...translation } : base;
  const ui = isEn
    ? {
        quoteOnline: "Online quote",
        sample: "Order a sample",
        gallery: "Project gallery —",
        specs: "Technical specification —",
        specsCaption: "Technical parameters:",
        quote: "Quote —",
        quoteNote:
          "This product is quoted individually. Tell us the shape, quantity and material — we will reply with an offer. Prices of 3D domed stickers are calculated instantly in the online calculator.",
        askQuote: "Request a quote",
        calculator: "3D sticker calculator",
        faq: "Questions and answers —",
        applications: "Applications",
        related: "Related products",
      }
    : {
        quoteOnline: "Wycena online",
        sample: "Zamów próbkę",
        gallery: "Galeria realizacji —",
        specs: "Specyfikacja techniczna —",
        specsCaption: "Parametry techniczne:",
        quote: "Wycena —",
        quoteNote:
          "Ten produkt wyceniamy indywidualnie. Napisz, jaki masz kształt, nakład i materiał — odpowiemy z ofertą. Ceny naklejek wypukłych 3D policzysz od razu w kalkulatorze online.",
        askQuote: "Poproś o wycenę",
        calculator: "Kalkulator naklejek 3D",
        faq: "Pytania i odpowiedzi —",
        applications: "Zastosowania",
        related: "Produkty pokrewne",
      };

  const url = `${BASE_URL}/${product.slug}/`;
  const related = base.related
    .map((s) => getProduct(s))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.metaDescription,
    category: "Naklejki reklamowe 3D",
    brand: { "@type": "Brand", name: "Naklejki-3d.com.pl" },
    manufacturer: { "@type": "Organization", name: "DRUKEXPERT.PL Sp. z o.o." },
    url,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: product.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <Layout breadcrumbTitle={product.name}>
      <SEOHead title={product.metaTitle} description={product.metaDescription} noindex={product.hidden} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <article>
        {/* Nagłówek + opis */}
        <section className="py-8 md:py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight">
              {product.h1}
            </h1>
            <p className="text-base md:text-lg text-foreground leading-relaxed mb-6 border-l-4 border-accent pl-4">
              <strong>{product.keyword}</strong> {product.lead}
            </p>
            <div className="space-y-4">
              {product.paragraphs.map((p, i) => (
                <p key={i} className="text-muted-foreground text-base md:text-lg leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link
                to="/kalkulator"
                className="gradient-gold text-accent-foreground font-bold px-6 py-3 rounded-lg inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-gold"
              >
                {ui.quoteOnline} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <Link
                to="/kontakt"
                className="border-2 border-border text-foreground font-semibold px-6 py-3 rounded-lg hover:border-accent transition-colors inline-flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" aria-hidden="true" /> {ui.sample}
              </Link>
            </div>
          </div>
        </section>

        {/* Galeria realizacji */}
        <section className="py-8 md:py-14 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-xl md:text-3xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Images className="w-6 h-6 text-accent" aria-hidden="true" />
              {ui.gallery} {product.name.toLowerCase()}
            </h2>
            {product.galleryNote ? (
              <p className="text-muted-foreground">{product.galleryNote}</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {galleryImages.map((img) => (
                  <img
                    key={img.src}
                    src={img.src}
                    alt={img.alt}
                    width={800}
                    height={800}
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-square object-cover rounded-xl border border-border"
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Specyfikacja */}
        <section className="py-8 md:py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl md:text-3xl font-bold text-foreground mb-6">
              {ui.specs} {product.name.toLowerCase()}
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <caption className="sr-only">{ui.specsCaption} {product.name}</caption>
                <tbody>
                  {product.specs.map((s, i) => (
                    <tr key={s.label} className={i % 2 ? "bg-secondary/40" : "bg-card"}>
                      <th scope="row" className="text-left font-semibold text-foreground p-3 md:p-4 w-2/5 align-top">
                        {s.label}
                      </th>
                      <td className="p-3 md:p-4 text-muted-foreground align-top">{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Kalkulator */}
        <section className="py-8 md:py-14 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-2xl">
            {product.quote ? (
              <QuickQuote
                heading={`${ui.quote} ${product.name.toLowerCase()}`}
                defaultWidth={product.quote.w}
                defaultHeight={product.quote.h}
                defaultQty={product.quote.qty}
                defaultFoil={product.quote.foil}
              />
            ) : (
              <div className="bg-card border border-border rounded-2xl p-6 text-center">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-3">
                  {ui.quote} {product.name.toLowerCase()}
                </h2>
                <p className="text-muted-foreground mb-5">
                  {ui.quoteNote}
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/kontakt"
                    className="gradient-gold text-accent-foreground font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity shadow-gold"
                  >
                    {ui.askQuote}
                  </Link>
                  <Link
                    to="/kalkulator"
                    className="border-2 border-border text-foreground font-semibold px-6 py-3 rounded-lg hover:border-accent transition-colors"
                  >
                    {ui.calculator}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* FAQ */}
        <section className="py-8 md:py-14">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-xl md:text-3xl font-bold text-foreground mb-6">
              {ui.faq} {product.name.toLowerCase()}
            </h2>
            <div className="space-y-3">
              {product.faqs.map((faq) => (
                <details key={faq.q} className="bg-card border border-border rounded-xl group">
                  <summary className="p-4 md:p-5 font-semibold text-foreground cursor-pointer list-none flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold">{faq.q}</h3>
                    <span className="text-accent group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                  </summary>
                  <div className="px-4 md:px-5 pb-5 text-muted-foreground leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Zastosowania */}
        <section className="py-8 md:py-14 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl md:text-3xl font-bold text-foreground mb-6">{ui.applications}</h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {product.applications.map((a) => (
                <li key={a.href}>
                  <Link
                    to={a.href}
                    className="flex items-start gap-3 bg-card border border-border rounded-xl p-4 hover:border-accent transition-colors h-full"
                  >
                    <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" aria-hidden="true" />
                    <span>
                      <span className="block font-semibold text-foreground">{a.label}</span>
                      <span className="block text-sm text-muted-foreground">{a.text}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Produkty pokrewne */}
        {related.length > 0 && (
          <section className="py-8 md:py-14">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-xl md:text-3xl font-bold text-foreground mb-6">{ui.related}</h2>
              <ul className="grid md:grid-cols-3 gap-3">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={`/${r.slug}`}
                      className="block bg-card border border-border rounded-xl p-4 hover:border-accent transition-colors h-full"
                    >
                      <span className="block font-semibold text-foreground mb-1">
                        {(isEn && PRODUCTS_EN[r.slug]?.name) || r.name}
                      </span>
                      <span className="block text-sm text-muted-foreground">
                        {((isEn && PRODUCTS_EN[r.slug]?.metaDescription) || r.metaDescription).slice(0, 90)}…
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
};

export const PRODUCT_SLUGS = PRODUCTS.map((p) => p.slug);

export default ProductPage;
